'use client';

import React, {
  createContext,
  Dispatch,
  RefObject,
  SetStateAction,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import checkoutReducer from './reducer';
import {
  IBranch,
  IComputeData,
  IDiscounts,
  IOrderPayload,
} from '@/_types';
import { IRedux } from '@/_store/types';
import { useSelector } from 'react-redux';

interface IPrice {
  all: number;
  core: number;
  withCashback: number;
  withDeposit: number;
}

export type TElemKey = 'payment_type' | 'phone' | 'in_stop' | 'address' | '';

export type TodoContextType = {
  values: IOrderPayload;
  totalPrice: number;
  serviceFee: number;
  validationFocus: TElemKey;
  computeData: IComputeData | null;
  deliveryPrice: number;
  calculatedPrice: IPrice;
  branch: IBranch | null;
  promocode: string;
  appliedPromoCode: string;
  refs: Record<TElemKey, RefObject<HTMLDivElement>> | null;
  usableWithOthers?: boolean;
  autogeneratedPromocodeID?: string;
  hasActiveBranch?: boolean;
  isPaymentSheet: boolean;
  cashbackAmount: number;
  depositAmount: number;
  discounts: IDiscounts | null;
  isOffHours: boolean;
  isGlobalLoading: boolean;
  setOffHours: Dispatch<SetStateAction<boolean>>;
  setServiceFee: Dispatch<SetStateAction<number>>;
  setValidationFocus: Dispatch<SetStateAction<TElemKey>>;
  setAutogeneratedPromocodeID: Dispatch<SetStateAction<string>>;
  setHasActiveBranch: Dispatch<SetStateAction<boolean>>;
  setDiscounts: Dispatch<SetStateAction<IDiscounts>>;
  setPromocode: Dispatch<string>;
  setAppliedPromoCode: Dispatch<string>;
  setIsPaymentSheet: Dispatch<boolean>;
  setUsableWithOrders: Dispatch<boolean>;
  setCashback: Dispatch<number>;
  setDeposit: Dispatch<number>;
  setBranch: (e: IBranch) => void;
  setGlobalLoading: Dispatch<SetStateAction<boolean>>;
  setComputeData: (e: IComputeData) => void;
  setTotalPrice: (e: number) => void;
  setDeliveryPrice: (e: number) => void;
  setValues: (fields: Partial<IOrderPayload>) => void;
  reset: (data?: IOrderPayload) => void;
};

const initialState: IOrderPayload = {
  apartment: '',
  building: '',
  description: '',
  accommodation: '',
  aggregator_id: null,
  delivery_type: '',
  client_id: '',
  delivery_price: 0,
  co_delivery_price: 0,
  given_cash: '',
  order_type_id: '',
  extra_phone_number: '',
  floor: '',
  is_operator_call: 'none',
  is_courier_call: true,
  fare_id: '',
  future_time: null,
  is_preorder: false,
  payment_type_id: '',
  is_cancel_old_order: false,
  is_reissued: false,
  leave_at_door: false,
  is_for_friend: false,
  friend_phone: '',
  paid: false,
  payment: {},
  source: '',
  steps: [
    {
      address: '',
      branch_id: '',
      branch_name: '',
      description: '',
      destination_address: '',
      location: {
        lat: null,
        long: null,
      },
      phone_number: '',
      products: [],
      step_discount_price: 0,
    },
  ],
  should_card_save: false,
  to_location: { lat: 0, long: 0 },
  to_address: '',
  to_address_description: '',
};

export const Context = createContext<TodoContextType>({
  values: initialState,
  totalPrice: 0,
  validationFocus: '',
  computeData: null,
  deliveryPrice: 0,
  hasActiveBranch: false,
  calculatedPrice: { all: 0, core: 0, withCashback: 0, withDeposit: 0 },
  branch: null,
  refs: null,
  isPaymentSheet: false,
  discounts: null,
  cashbackAmount: 0,
  depositAmount: 0,
  serviceFee: 0,
  autogeneratedPromocodeID: '',
  promocode: '',
  appliedPromoCode: '',
  usableWithOthers: false,
  isOffHours: false,
  isGlobalLoading: false,
  setGlobalLoading: () => {},
  setOffHours: () => {},
  setServiceFee: () => {},
  setValidationFocus: () => {},
  setHasActiveBranch: () => {},
  setDiscounts: () => {},
  setPromocode: () => {},
  setAppliedPromoCode: () => {},
  setCashback: () => {},
  setDeposit: () => {},
  setAutogeneratedPromocodeID: () => {},
  reset: () => {},
  setValues: () => {},
  setBranch: () => {},
  setComputeData: () => {},
  setIsPaymentSheet: () => {},
  setTotalPrice: () => {},
  setDeliveryPrice: () => {},
  setUsableWithOrders: () => {},
});

export default function CheckoutProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isGlobalLoading, setGlobalLoading] = useState<boolean>(false);
  const [computeData, setComputeData] = useState<IComputeData | null>(null);
  const [branch, setBranch] = useState<IBranch | null>(null);
  const [isPaymentSheet, setIsPaymentSheet] = useState<boolean>(false);
  const [usableWithOthers, setUsableWithOrders] = useState<boolean>(false);
  const [hasActiveBranch, setHasActiveBranch] = useState<boolean>(false);
  const [isOffHours, setOffHours] = useState(false);

  const [totalPrice, setTotalPrice] = useState(0);
  const [serviceFee, setServiceFee] = useState(0);
  const [promocode, setPromocode] = useState('');
  const [appliedPromoCode, setAppliedPromoCode] = useState('');
  const [validationFocus, setValidationFocus] = useState<TElemKey>('');
  const [cashbackAmount, setCashback] = useState(0);
  const [autogeneratedPromocodeID, setAutogeneratedPromocodeID] = useState('');
  const [depositAmount, setDeposit] = useState(0);
  const [discounts, setDiscounts] = useState<IDiscounts>({});
  const [state, dispatch] = useReducer(checkoutReducer, initialState);
  const [deliveryPrice, setDeliveryPrice] = useState(0);

  const { order_type } = useSelector((state: IRedux) => state.session);

  const refs = {
    payment_type: useRef<HTMLDivElement>(null),
    phone: useRef<HTMLDivElement>(null),
    in_stop: useRef<HTMLDivElement>(null),
    address: useRef<HTMLDivElement>(null),
  } as Record<TElemKey, RefObject<HTMLDivElement>>;

  const calculatedPrice: IPrice = useMemo(() => {
    const core = discounts?.all_discount_price
      ? totalPrice +
        (discounts?.is_delivery_free
          ? 0
          : order_type?.type === 'delivery'
            ? deliveryPrice
            : 0) +
        (discounts?.all_discount_price || 0)
      : discounts?.is_delivery_free
        ? totalPrice
        : totalPrice + (order_type?.type === 'delivery' ? deliveryPrice : 0);

    return {
      all:
        core -
        Number(cashbackAmount || 0) -
        Number(depositAmount || 0) +
        (serviceFee || 0),
      core,
      withCashback: core - Number(cashbackAmount || 0),
      withDeposit: core - Number(depositAmount || 0),
    };
  }, [
    discounts?.all_discount_price,
    discounts?.is_delivery_free,
    cashbackAmount,
    depositAmount,
    deliveryPrice,
    order_type?.type,
    totalPrice,
    promocode,
    serviceFee,
  ]);

  return (
    <Context.Provider
      value={{
        isGlobalLoading,
        isOffHours,
        branch,
        totalPrice,
        computeData,
        validationFocus,
        calculatedPrice,
        deliveryPrice,
        refs,
        values: state,
        cashbackAmount,
        autogeneratedPromocodeID,
        depositAmount,
        discounts,
        promocode,
        appliedPromoCode,
        usableWithOthers,
        isPaymentSheet,
        hasActiveBranch,
        serviceFee,
        setGlobalLoading,
        setDiscounts,
        setPromocode,
        setAppliedPromoCode,
        setAutogeneratedPromocodeID,
        setValidationFocus,
        setCashback,
        setDeposit,
        setIsPaymentSheet,
        setUsableWithOrders,
        setHasActiveBranch,
        setServiceFee,
        setOffHours,
        setBranch: (val: IBranch) => setBranch(val),
        setComputeData: (val: IComputeData) => setComputeData(val),
        setTotalPrice: (val: number) => setTotalPrice(val),
        setDeliveryPrice: (val: number) => setDeliveryPrice(val),
        reset: (fields) =>
          dispatch({ type: 'RESET', payload: fields ?? initialState }),
        setValues: (fields) => {
          {
            console.log('SET_VALUES', { fields });
            dispatch({ type: 'SET_VALUES', payload: fields });
          }
        },
      }}
    >
      {children}
    </Context.Provider>
  );
}
